import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { API, AreaCategoryDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { iif } from 'rxjs';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit';

@Component({
  selector: 'sb-page-area-categories',
  templateUrl: './area-categories.component.html',
  styleUrls: ['./area-categories.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaCategoriesComponent extends RequestClass implements ModalNavigationComponent {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() isInIntegratedContext: boolean;

  @Output() selectCategory: EventEmitter<AreaCategoryDTO> = new EventEmitter<AreaCategoryDTO>();

  // global variables
  showSideBar = false;
  isInModalContext: boolean;

  // table wrapper variables
  areaCategories: AreaCategoryDTO[] = undefined;
  selectedAreaCategory: AreaCategoryDTO = null;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_md_AreaCategory_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (categories: AreaCategoryDTO[]) => {
          this.areaCategories = this.buildSplitButtons(categories);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  addAreaCategory() {
    this.selectedAreaCategory = new AreaCategoryDTO();
    this.toggleSideBar();
  }

  editAreaCategory(areaCategory: AreaCategoryDTO) {
    this.selectedAreaCategory = areaCategory;
    this.toggleSideBar();
  }

  updateAreaCategory(areaCategory: AreaCategoryDTO) {
    iif(
      () => !areaCategory.id,
      this.api.ca_md_AreaCategory_createCategory(areaCategory),
      this.api.ca_md_AreaCategory_editCategory(areaCategory)
    )
      .pipe(take(1))
      .subscribe(() => {
        this.resetView();
      });
  }

  resetView() {
    this.toggleSideBar();
    this.selectedAreaCategory = null;
    this.requestDebounced();
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.area_category_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          action: 'edit',
          tooltip: 'lang.globals.editAreaCategory',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(category: AreaCategoryDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editAreaCategory(category);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
